<template>
    <ClientOnly>
        <svg
            :viewBox="viewBox"
            :width="effectiveOpts.width"
            :height="effectiveOpts.height"
            :class="effectiveOpts.svgClass"
            role="img"
            aria-label="Random blob"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                ref="pathEl"
                class="blob-path main-path"
                :d="currentD"
                :fill="effectiveOpts.fill"
                :stroke="effectiveOpts.stroke"
                :stroke-width="effectiveOpts.strokeWidth"
            />
            <path
                ref="secondaryPathEl"
                aria-hidden="true"
                class="blob-path"
                :d="secondaryD"
                :fill="effectiveOpts.fill"
                :stroke="effectiveOpts.stroke"
                :stroke-width="effectiveOpts.strokeWidth"
                style="opacity: 0"
            />
        </svg>

        <div class="top-0 left-0 absolute before:bg-accent/15 after:bg-accent/50 w-0 h-0 dot" />
    </ClientOnly>
</template>

<script lang="ts" setup>
    import { nextTick, watch } from "vue";
    // minimal typings for anime usage in this component
    type AnimeInstance = { pause?: () => void; finished?: Promise<unknown> };

    type Point = { x: number; y: number };

    type BlobOptions = {
        width?: number | string; // SVG width
        height?: number | string; // SVG height
        svgClass?: string;
        points?: number; // number of control points around the circle (6–24 is nice)
        variance?: number; // 0..1 how wobbly the outline is
        seed?: number | string; // optional seed for repeatable blobs
        padding?: number; // inner padding around edges
        fill?: string;
        stroke?: string;
        strokeWidth?: number;
        curviness?: number; // 0..1 how rounded the shape is
        closed?: boolean; // keep true for a blob
        viewbox?: {
            width: number;
            height: number;
        };
    };

    const props = withDefaults(defineProps<BlobOptions>(), {
        width: 400,
        height: 300,
        svgClass: undefined,
        seed: undefined,
        points: undefined,
        variance: undefined,
        padding: undefined,
        curviness: undefined,
        fill: undefined,
        stroke: "none",
        strokeWidth: 0,
        closed: true,
        viewbox: undefined,
    });

    // ---------- helpers (module-level, reused) ----------
    function easeInOut(t: number) {
        return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    function pointsToSmoothPath(pts: Point[], cfg: { closed: boolean; curviness: number }): string {
        const { closed, curviness } = cfg;
        const n = pts.length;
        if (!pts || n === 0) return "";
        const m = Math.max(0, Math.min(1, curviness)) / 6; // 0..~0.1667

        const get = (i: number) => {
            return closed ? pts[(i + n) % n] : pts[Math.max(0, Math.min(n - 1, i))];
        };

        let d = `M ${pts[0]!.x.toFixed(2)} ${pts[0]!.y.toFixed(2)}`;

        for (let i = 0; i < (closed ? n : n - 1); i++) {
            const p0 = get(i - 1)!;
            const p1 = get(i)!;
            const p2 = get(i + 1)!;
            const p3 = get(i + 2)!;

            const c1x = p1.x + (p2.x - p0.x) * m;
            const c1y = p1.y + (p2.y - p0.y) * m;
            const c2x = p2.x - (p3.x - p1.x) * m;
            const c2y = p2.y - (p3.y - p1.y) * m;

            d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
        }

        if (closed) d += " Z";
        return d;
    }

    function hashStringToUint32(input: string): number {
        // simple string hash -> uint32
        let h = 2166136261 >>> 0;
        for (let i = 0; i < input.length; i++) {
            h ^= input.charCodeAt(i);
            h = Math.imul(h, 16777619) >>> 0;
        }
        return h >>> 0;
    }

    function createRngFromSeed(seed?: number | string): () => number {
        if (seed === undefined || seed === null) return Math.random;
        const s = typeof seed === "number" ? seed >>> 0 : hashStringToUint32(String(seed));
        let t = s >>> 0;
        return () => {
            t += 0x6d2b79f5;
            let r = Math.imul(t ^ (t >>> 15), 1 | t);
            r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
            return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
        };
    }

    type BlobResult = {
        path: string;
        viewBox: string;
        points: Point[];
        secondaryPath?: string;
    };

    function createRandomSvgBlob(opts: BlobOptions = {}): BlobResult {
        const { points = 12, variance = 0.35, seed, padding = 8, curviness = 0.8, closed = true } = opts;

        const viewbox = props.viewbox ?? { width: 300, height: 300 };

        const rng = createRngFromSeed(seed);

        const cx = viewbox.width / 2;
        const cy = viewbox.height / 2;
        const maxR = Math.max(0, Math.min(viewbox.width, viewbox.height) / 2 - padding);
        const minR = maxR * (1 - Math.min(Math.max(variance, 0), 1)); // keep a base radius

        // generate first set of points
        const P1: Point[] = [];
        for (let i = 0; i < points; i++) {
            const theta = (i / points) * Math.PI * 2;
            const r = minR + (maxR - minR) * easeInOut(rng());
            P1.push({ x: cx + Math.cos(theta) * r, y: cy + Math.sin(theta) * r });
        }

        // generate second set of points (use same rng so shapes are related but different)
        const P2: Point[] = [];
        for (let i = 0; i < points; i++) {
            const theta = (i / points) * Math.PI * 2;
            const r = minR + (maxR - minR) * easeInOut(rng());
            P2.push({ x: cx + Math.cos(theta) * r, y: cy + Math.sin(theta) * r });
        }

        if (P1.length < 3 || P2.length < 3) throw new Error("Need at least 3 points to create a blob.");

        const d1 = pointsToSmoothPath(P1, { closed, curviness });
        const d2 = pointsToSmoothPath(P2, { closed, curviness });
        const viewBox = `0 0 ${viewbox.width} ${viewbox.height}`;

        // return primary path and secondary path (secondary will be rendered with opacity 0)
        return { path: d1, viewBox, points: P1, secondaryPath: d2 } as unknown as BlobResult;
    }

    // create blob using component props. Apply dynamic defaults at runtime
    const effectiveOpts = computed<BlobOptions>(() => ({
        width: props.width ?? 400,
        height: props.height ?? 300,
        seed: props.seed ?? generateRandomSeed(),
        points: props.points ?? generateRandomNumber(10, 15),
        variance: props.variance ?? generateRandomNumber(0.25, 0.75, 2),
        padding: props.padding ?? generateRandomNumber(5, 10),
        curviness: props.curviness ?? generateRandomNumber(0.5, 0.9, 2),
        fill: props.fill,
        stroke: props.stroke ?? "none",
        strokeWidth: props.strokeWidth ?? 0,
        closed: props.closed ?? true,
        svgClass: props.svgClass ?? "",
    }));

    const blob = computed(() => createRandomSvgBlob(effectiveOpts.value));
    const viewBox = computed(() => blob.value.viewBox);

    // animation state for morphing between blob shapes

    const pathEl = ref<SVGPathElement | null>(null);
    const secondaryPathEl = ref<SVGPathElement | null>(null);
    const currentD = ref<string>(blob.value.path);
    const secondaryD = computed(() => blob.value.secondaryPath ?? "");
    let animInstance: AnimeInstance | null = null;

    function runAnimation(newBlob: BlobResult, oldBlob?: BlobResult) {
        // if no previous blob or missing path element, set immediately
        if (!oldBlob || !pathEl.value) {
            currentD.value = newBlob.path;
            return;
        }

        // cancel previous animation if running
        if (animInstance && typeof animInstance.pause === "function") animInstance.pause();

        // lazy-load anime only when needed to avoid static import/type issues
        (async () => {
            try {
                const mod = await import("animejs");
                const animate = mod.animate;
                const morphTo = mod.svg.morphTo;
                // const createMotionPath = mod.svg.createMotionPath;

                // ensure the secondary path DOM contains the new path data before morphing
                if (secondaryPathEl.value) secondaryPathEl.value.setAttribute("d", newBlob.secondaryPath ?? "");
                await nextTick();

                // Morph the `d` attribute of the primary path into the secondary path
                animInstance = animate(pathEl.value!, {
                    d: morphTo(secondaryPathEl.value!),
                    ease: "outElastic",
                    duration: 1250,
                    complete: () => {
                        // after morph completes, set current path to the new primary path
                        currentD.value = newBlob.path;
                        animInstance = null;
                    },
                }) as unknown as AnimeInstance;
            } catch {
                // fallback: immediately set path
                currentD.value = newBlob.path;
                animInstance = null;
            }
        })();
    }

    // watch for blob changes (which reflect effectiveOpts changes) and run morph
    watch(blob, (newB, oldB) => {
        runAnimation(newB, oldB as BlobResult | undefined);
    });
</script>

<style scoped>
    @reference 'tailwindcss';
</style>
